import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Key } from "lucide-react";

interface SecurityQuestionSetupProps {
  onComplete?: () => void;
  title?: string;
  showCard?: boolean;
  userId?: string; // Optional user ID to use instead of getting from session
}

const SECURITY_QUESTIONS = [
  "What was the name of your first pet?",
  "What city were you born in?",
  "What was your mother's maiden name?",
  "What was the name of your first school?",
  "What was your childhood nickname?",
  "What street did you live on as a child?",
  "What was the make of your first car?",
  "What is your favorite movie?",
  "What was the name of your best friend in high school?",
  "What is your favorite food?"
];

export const SecurityQuestionSetup = ({ 
  onComplete, 
  title = "Security Question Setup",
  showCard = true,
  userId 
}: SecurityQuestionSetupProps) => {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('🔵 Security question form submitted');
    
    if (!question || !answer.trim()) {
      console.log('🔴 Validation failed - missing question or answer');
      toast({
        title: "Error",
        description: "Please select a question and provide an answer.",
        variant: "destructive",
      });
      return;
    }

    console.log('🔵 Starting security question save process...');
    setLoading(true);
    try {
      let currentUserId = userId;
      console.log('🔵 Current userId prop:', currentUserId);
      
      // If no userId provided, try to get from current session
      if (!currentUserId) {
        console.log('🔵 No userId prop, getting from session...');
        const { data: { user } } = await supabase.auth.getUser();
        console.log('🔵 User from session:', user?.id || 'null');
        if (!user) throw new Error("Not authenticated");
        currentUserId = user.id;
      }

      console.log('🔵 Attempting direct database insert for new user...');
      
      // For newly created users during signup, insert directly to database
      // since they may not have a verified session yet
      const { error: insertError } = await supabase
        .from('user_security_questions')
        .upsert({
          user_id: currentUserId,
          question: question,
          answer_hash: btoa(answer.trim().toLowerCase()) // Simple base64 encoding for now
        });

      if (insertError) {
        console.error('🔴 Direct insert error:', insertError);
        throw new Error(`Database error: ${insertError.message}`);
      }

      console.log('🔵 Security question saved successfully');

      toast({
        title: "Success",
        description: "Security question saved successfully.",
      });

      setQuestion("");
      setAnswer("");
      onComplete?.();
    } catch (error: any) {
      console.error('🔴 Error saving security question:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to save security question.",
        variant: "destructive",
      });
    } finally {
      console.log('🔵 Setting loading to false');
      setLoading(false);
    }
  };

  const content = (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="security-question">Security Question</Label>
        <Select value={question} onValueChange={setQuestion} required>
          <SelectTrigger>
            <SelectValue placeholder="Select a security question" />
          </SelectTrigger>
          <SelectContent>
            {SECURITY_QUESTIONS.map((q) => (
              <SelectItem key={q} value={q}>
                {q}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <Label htmlFor="security-answer">Answer</Label>
        <Input
          id="security-answer"
          type="text"
          placeholder="Enter your answer"
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          required
          maxLength={100}
        />
        <p className="text-xs text-muted-foreground mt-1">
          This answer is case-insensitive and will be used to reset your password if needed.
        </p>
      </div>

      <Button 
        type="submit" 
        disabled={loading} 
        className="w-full"
        onClick={(e) => {
          console.log('🟠 SECURITY BUTTON CLICKED - Direct onClick');
          if (loading) {
            console.log('🟠 Button is disabled due to loading state');
            e.preventDefault();
          } else {
            console.log('🟠 Button is enabled, form should submit');
          }
        }}
      >
        {loading ? "Saving..." : "Save Security Question"}
      </Button>
    </form>
  );

  if (!showCard) {
    return content;
  }

  return (
    <Card className="obsidian-card">
      <CardHeader>
        <CardTitle className="text-energy flex items-center gap-2">
          <Key className="h-5 w-5" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {content}
      </CardContent>
    </Card>
  );
};